import { getTeamSideAugmentGroup, isOneGoalAugmentCode, parseAugmentRef } from './augments';

/**
 * Merge home/away augment variant stats into pooled UI keys (e.g. +1h/+1a → +1goal).
 * Raw per-side counts stay in storage/API; call this only when rendering stats.
 * @param {Record<string, { total?: number, successful?: number }>|null|undefined} augments
 * @returns {Record<string, { total: number, successful: number }>}
 */
export function groupAugmentStatsForUi(augments) {
  if (!augments || typeof augments !== 'object') return {};

  const grouped = {};
  for (const [rawKey, entry] of Object.entries(augments)) {
    const { code } = parseAugmentRef(rawKey);
    if (!code) continue;
    const group = getTeamSideAugmentGroup(code);
    const key = isOneGoalAugmentCode(code) ? 'g' : group ? group.uiValue : code;
    if (!grouped[key]) {
      grouped[key] = { total: 0, successful: 0 };
    }
    grouped[key].total += Number(entry?.total) || 0;
    grouped[key].successful += Number(entry?.successful) || 0;
  }

  // Sacrifice payoff is the linked Multiplier or Bounty; never show d ahead of those.
  if (grouped.d) {
    const followSuccessful =
      (Number(grouped.m?.successful) || 0) + (Number(grouped.b?.successful) || 0);
    grouped.d.successful = Math.min(grouped.d.total, followSuccessful);
  }

  return grouped;
}

/** Success rate for augment stats (0 when unused). */
export function augmentSuccessRate(entry) {
  const total = Number(entry?.total) || 0;
  if (!total) return 0;
  return (Number(entry?.successful) || 0) / total;
}

/** Sort augment stat entries by success rate (high → low), then by total uses. */
export function compareAugmentStatsBySuccess(aEntry, bEntry) {
  const rateDiff = augmentSuccessRate(bEntry) - augmentSuccessRate(aEntry);
  if (rateDiff !== 0) return rateDiff;
  return (Number(bEntry?.total) || 0) - (Number(aEntry?.total) || 0);
}

export function formatBettingPct(numerator, denominator) {
  if (!denominator) return '0%';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

const ROUND_AUGMENT_STAT_RULES = {
  sn: 'Wrong outcomes on placed slots ÷ Safety net slots placed.',
  bus: 'Correct zero sides ÷ zero sides predicted.',
  aon: 'Successful sweeps ÷ All or nothing picks.',
  os: 'Successful scores ÷ 2+ GD picks.',
  gc: 'Picks that scored bonus ÷ Goals carnival picks.',
  bl: 'Near misses (wrong 1X2, off by 1 goal) ÷ finished picks.'
};

/** Hover text for one match-play success chip in round recap. */
export function getMatchAugmentStatTooltip(name, counts) {
  const total = Number(counts?.total) || 0;
  const successful = Number(counts?.successful) || 0;
  const pct = formatBettingPct(successful, total);
  return `${name}: ${successful}/${total} (${pct}). Times the play applied ÷ times it was used.`;
}

/** Hover text for one round-play success chip in round recap. */
export function getRoundAugmentStatTooltip(name, code, counts) {
  const base = parseAugmentRef(code).code;
  const total = Number(counts?.total) || 0;
  const successful = Number(counts?.successful) || 0;
  const pct = formatBettingPct(successful, total);
  const rule = ROUND_AUGMENT_STAT_RULES[base] || 'Successful uses ÷ total uses.';
  return `${name}: ${successful}/${total} (${pct}). ${rule}`;
}

export const MATCH_PLAY_SUCCESS_SECTION_TOOLTIP =
  'How often each match play applied this round (applied ÷ used), pooled across the league. Hover an icon for details.';

export const ROUND_PLAY_SUCCESS_SECTION_TOOLTIP =
  'How often each round play paid off this round, pooled across the league. Hover an icon for how that play is counted.';

export function formatAvgMatchPoints(totalPoints, totalBets) {
  if (!totalBets) return '0';
  const avg = Number(totalPoints) / Number(totalBets);
  const rounded = Math.round(avg * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function summarizePointBuckets(pointBuckets) {
  let totalPoints = 0;
  let totalPlayers = 0;
  if (!pointBuckets || typeof pointBuckets !== 'object') {
    return { totalPoints, totalPlayers };
  }
  for (const [scoreStr, count] of Object.entries(pointBuckets)) {
    const score = Number(scoreStr);
    const n = Number(count) || 0;
    if (!Number.isFinite(score) || n <= 0) continue;
    totalPoints += score * n;
    totalPlayers += n;
  }
  return { totalPoints, totalPlayers };
}

/** Players represented in the round point-bucket histogram. */
export function countPlayersFromPointBuckets(pointBuckets) {
  return summarizePointBuckets(pointBuckets).totalPlayers;
}

/** Floored mean of player round totals from the point-bucket histogram. */
export function formatAvgPlayerRoundPoints(pointBuckets) {
  const { totalPoints, totalPlayers } = summarizePointBuckets(pointBuckets);
  if (!totalPlayers) return '0';
  return String(Math.floor(totalPoints / totalPlayers));
}

export function hasBettingStats(stats) {
  return stats != null && Number(stats.totalBets) > 0;
}

export function hasPickStats(pickStats) {
  return hasBettingStats(pickStats);
}

export function getMatchOutcome(homeScore, awayScore) {
  const h = Number(homeScore);
  const a = Number(awayScore);
  if (!Number.isFinite(h) || !Number.isFinite(a)) return null;
  if (h > a) return 'home';
  if (a > h) return 'away';
  return 'draw';
}

/**
 * Derive live/final outcome and exact-score hit counts from lock snapshot + current score.
 * @returns {{ outcomeHits: number, resultHits: number, totalBets: number }}
 */
export function livePickCounts(pickStats, homeScore, awayScore) {
  const totalBets = Number(pickStats?.totalBets) || 0;
  if (!totalBets) return { outcomeHits: 0, resultHits: 0, totalBets: 0 };

  const outcome = getMatchOutcome(homeScore, awayScore);
  const outcomeHits =
    outcome && pickStats?.betsOutcome ? Number(pickStats.betsOutcome[outcome]) || 0 : 0;

  const resultKey = `${Number(homeScore)}-${Number(awayScore)}`;
  const resultHits =
    pickStats?.betsResults && pickStats.betsResults[resultKey] != null
      ? Number(pickStats.betsResults[resultKey]) || 0
      : 0;

  return { outcomeHits, resultHits, totalBets };
}
